/**
 * OCC Context Header — the "← Back to Operations Command Centre" ribbon.
 *
 * Rendered globally by <Shell> whenever `_from=occ` is present in the
 * hash query. Provides:
 *   • Back link to the OCC.
 *   • The queue label ("Media Review Queue", "Policy Review Queue", …).
 *   • A summary of the applied filters, so the operator sees exactly
 *     what is filtered without having to inspect the sidebar controls.
 *
 * Contract: this banner is presentation-only. Actual queue filtering
 * is the receiving workspace's responsibility (via `useOccContext`).
 * The banner is shown even when a workspace has not yet been wired to
 * apply filters — so the operator always knows *why* they arrived and
 * *what* the intent was. This satisfies OCC-1 "no orphan destinations".
 */

import React from "react";
import { ArrowLeft, Filter } from "lucide-react";
import { useOccContext } from "@/lib/occContext";
import { TESTIDS } from "@/lib/testIds";

const HIDDEN_PARAM_KEYS = new Set(["_from", "queue", "focus", "cursor"]);

function prettyKey(k) {
  return k.replace(/_/g, " ");
}

function prettyVal(v) {
  return String(v).replace(/_/g, " ");
}

export default function OccContextHeader() {
  const { fromOcc, queueLabel, params, exit } = useOccContext();
  if (!fromOcc) return null;

  const filters = Object.entries(params).filter(([k, v]) => !HIDDEN_PARAM_KEYS.has(k) && v !== "");

  return (
    <div
      data-testid={TESTIDS.occContextHeader}
      className="mb-4 rounded-xl border border-indigo-200 bg-indigo-50/70 px-4 py-3 flex flex-wrap items-center gap-x-4 gap-y-2 cc-fade-up"
    >
      <button
        type="button"
        onClick={exit}
        data-testid={TESTIDS.occContextBack}
        className="inline-flex items-center gap-1.5 text-[12px] font-semibold text-indigo-800 hover:text-indigo-900 hover:underline"
      >
        <ArrowLeft className="w-3.5 h-3.5" />
        Back to Operations Command Centre
      </button>

      <span className="text-indigo-300 select-none">·</span>

      <div className="flex flex-col">
        <span className="text-[9.5px] font-bold uppercase tracking-[0.16em] text-indigo-600">
          You are viewing
        </span>
        <span
          className="text-[13.5px] font-semibold text-slate-900"
          data-testid={TESTIDS.occContextQueueLabel}
        >
          {queueLabel || "Filtered work queue"}
        </span>
      </div>

      {filters.length > 0 && (
        <div className="flex items-center gap-2 flex-wrap ml-auto">
          <Filter className="w-3 h-3 text-indigo-500" />
          {filters.map(([k, v]) => (
            <span
              key={k}
              data-testid={TESTIDS.occContextFilterChip(k)}
              className="inline-flex items-center gap-1 text-[10.5px] font-mono uppercase tracking-[0.08em] px-2 py-0.5 rounded-full border border-indigo-200 bg-white text-indigo-800"
            >
              <span className="opacity-60 font-sans normal-case">{prettyKey(k)}:</span>
              <span>{prettyVal(v)}</span>
            </span>
          ))}
        </div>
      )}
    </div>
  );
}
